import { colors } from '@ardoq/design-tokens';
import { PALE_LIST_FIELD_COLORS } from '../../consts';

const COLOR = {
  green: colors.green600,
  softGreen: colors.green400,
  orange: colors.yellow300,
  amber: colors.yellow500,
  red: colors.orange600,
};

const PALE_COLOR = PALE_LIST_FIELD_COLORS;

type ValueStartsWith = string;

const LOW_TO_HIGH_COLOR_MAP = new Map<ValueStartsWith, string>([
  ['1', COLOR.red],
  ['2', COLOR.amber],
  ['3', COLOR.orange],
  ['4', COLOR.softGreen],
  ['5', COLOR.green],
]);

const PALE_LOW_TO_HIGH_COLOR_MAP = new Map<ValueStartsWith, string>([
  ['1', PALE_COLOR.red],
  ['2', PALE_COLOR.amber],
  ['3', PALE_COLOR.orange],
  ['4', PALE_COLOR.softGreen],
  ['5', PALE_COLOR.green],
]);

const HIGH_TO_LOW_COLOR_MAP = new Map<ValueStartsWith, string>([
  ['1', COLOR.green],
  ['2', COLOR.softGreen],
  ['3', COLOR.orange],
  ['4', COLOR.amber],
  ['5', COLOR.red],
]);

const PALE_HIGH_TO_LOW_COLOR_MAP = new Map<ValueStartsWith, string>([
  ['1', PALE_COLOR.green],
  ['2', PALE_COLOR.softGreen],
  ['3', PALE_COLOR.orange],
  ['4', PALE_COLOR.amber],
  ['5', PALE_COLOR.red],
]);

const LIFECYCLE_PHASE_COLOR_MAP = new Map<string, string>([
  ['Evaluating', '#0592AC'],
  ['Implementing', '#6695CD'],
  ['Live', '#3BA063'],
  ['Mainstream', '#3BA063'],
  ['Review', COLOR.amber],
  ['Phasing Out', '#BB1919'],
  ['Retired', colors.grey600],
]);

const PALE_LIFECYCLE_PHASE_COLOR_MAP = new Map<string, string>([
  ['Evaluating', '#51CCE1'],
  ['Implementing', '#94B5DC'],
  ['Live', '#76BC92'],
  ['Mainstream', '#76BC92'],
  ['Review', PALE_COLOR.amber],
  ['Phasing Out', '#CF5E5E'],
  ['Retired', '#88898E'],
]);

const YES_NO_COLOR_MAP = new Map<string, string>([
  ['Yes', COLOR.green],
  ['No', COLOR.red],
]);

const PALE_YES_NO_COLOR_MAP = new Map<string, string>([
  ['Yes', PALE_COLOR.green],
  ['No', PALE_COLOR.red],
]);

// Map of list field names to their default value-start-to-color mappings
export const LIST_FIELD_DEFAULT_VALUE_START_TO_COLOR_MAP: Map<
  string,
  Map<ValueStartsWith, string>
> = new Map([
  // highlighted fields with low to high color mapping (1 red to 5 green)
  ['functional_fit', LOW_TO_HIGH_COLOR_MAP],
  ['technical_strategic_fit', LOW_TO_HIGH_COLOR_MAP],
  ['technical_integrity', LOW_TO_HIGH_COLOR_MAP],
  ['business_strategic_fit', LOW_TO_HIGH_COLOR_MAP],
  ['features_and_usability', LOW_TO_HIGH_COLOR_MAP],
  ['maintainability_and_agility', LOW_TO_HIGH_COLOR_MAP],

  // other fields with high to low color mapping
  ['intelligence_scanning_external_environment', HIGH_TO_LOW_COLOR_MAP],
  ['criticality', HIGH_TO_LOW_COLOR_MAP],
  ['coordination_standardizing_procedures', HIGH_TO_LOW_COLOR_MAP],
  ['control_conducting_audits', HIGH_TO_LOW_COLOR_MAP],
  ['policy_defining_the_vision', HIGH_TO_LOW_COLOR_MAP],
  ['control_allocating_resources', HIGH_TO_LOW_COLOR_MAP],
  ['control_monitoring_performance', HIGH_TO_LOW_COLOR_MAP],
  ['control_enforcing_standards', HIGH_TO_LOW_COLOR_MAP],
  ['control_adjusting_activities_on_feedback', HIGH_TO_LOW_COLOR_MAP],
  ['coordination_harmonizing_activities', HIGH_TO_LOW_COLOR_MAP],
  ['coordination_minimizing_conflicts', HIGH_TO_LOW_COLOR_MAP],
  ['coordination_monitoring_disruptions', HIGH_TO_LOW_COLOR_MAP],
  ['coordination_ensuring_consistent_comms', HIGH_TO_LOW_COLOR_MAP],
  ['intelligence_innovation', HIGH_TO_LOW_COLOR_MAP],
  ['intelligence_adapting_to_external_change', HIGH_TO_LOW_COLOR_MAP],
  ['operations_execution_of_tasks', HIGH_TO_LOW_COLOR_MAP],
  ['operations_resource_allocation', HIGH_TO_LOW_COLOR_MAP],
  ['operations_coordination', HIGH_TO_LOW_COLOR_MAP],
  ['operations_managing_local_issues', HIGH_TO_LOW_COLOR_MAP],
  ['policy_setting_goals_and_priorities', HIGH_TO_LOW_COLOR_MAP],
  ['policy_making_key_decisions', HIGH_TO_LOW_COLOR_MAP],
  ['policy_ensuring_organizational_coherence', HIGH_TO_LOW_COLOR_MAP],
  ['control_balancing_short_term_with_long_term', HIGH_TO_LOW_COLOR_MAP],
  ['intelligence_long_term_planning', HIGH_TO_LOW_COLOR_MAP],
  ['intelligence_balancing_current_needs_with_future', HIGH_TO_LOW_COLOR_MAP],
  ['operations_responding_to_demands', HIGH_TO_LOW_COLOR_MAP],
  ['policy_resolving_conflicts', HIGH_TO_LOW_COLOR_MAP],
  ['policy_maintaining_viability', HIGH_TO_LOW_COLOR_MAP],
]);

// Map of list field names to their default value-to-color mappings
export const LIST_FIELD_DEFAULT_VALUE_TO_COLOR_MAP: Map<
  string,
  Map<string, string>
> = new Map([
  // fields with custom color mappings
  [
    'objective_priority',
    new Map([
      ['High', COLOR.green],
      ['Medium', COLOR.orange],
      ['Low', COLOR.red],
    ]),
  ],
  [
    'application_ownership_state',
    new Map([
      ['Unowned', COLOR.red],
      ['Nominated', COLOR.amber],
      ['Owned', COLOR.orange],
      ['Managed', COLOR.green],
    ]),
  ],
  [
    'confidentiality',
    new Map([
      ['Restricted', COLOR.red],
      ['Confidential', COLOR.amber],
      ['Internal', COLOR.orange],
      ['Public', COLOR.green],
    ]),
  ],
  ['ownership_accepted', YES_NO_COLOR_MAP],
  ['saa_s_alternative', YES_NO_COLOR_MAP],
  ['pass', YES_NO_COLOR_MAP],
  [
    'data_proccessing_agreement',
    new Map([
      ['Yes', COLOR.green],
      ['No', COLOR.red],
      // TODO: to be clarified
      ['Not Applicable', '#3e3e3e'],
    ]),
  ],
  [
    'ai_risk_level',
    new Map([
      ['Unacceptable Risk', COLOR.red],
      ['High Risk', COLOR.amber],
      ['Limited Risk', COLOR.orange],
      ['Minimal Risk', COLOR.green],
    ]),
  ],
  [
    'strategic_rating',
    new Map([
      ['Invest', COLOR.green],
      ['Tolerate', COLOR.orange],
      ['Migrate', COLOR.amber],
      ['Eliminate', COLOR.red],
    ]),
  ],
  ['lifecycle_phase', LIFECYCLE_PHASE_COLOR_MAP],
]);

// Pale versions for Dependency Map
export const PALE_LIST_FIELD_DEFAULT_VALUE_START_TO_COLOR_MAP: Map<
  string,
  Map<ValueStartsWith, string>
> = new Map([
  // highlighted fields with low to high color mapping (1 red to 5 green)
  ['functional_fit', PALE_LOW_TO_HIGH_COLOR_MAP],
  ['technical_strategic_fit', PALE_LOW_TO_HIGH_COLOR_MAP],
  ['technical_integrity', PALE_LOW_TO_HIGH_COLOR_MAP],
  ['business_strategic_fit', PALE_LOW_TO_HIGH_COLOR_MAP],
  ['features_and_usability', PALE_LOW_TO_HIGH_COLOR_MAP],
  ['maintainability_and_agility', PALE_LOW_TO_HIGH_COLOR_MAP],

  // other fields with high to low color mapping
  ['intelligence_scanning_external_environment', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['criticality', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['coordination_standardizing_procedures', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['control_conducting_audits', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['policy_defining_the_vision', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['control_allocating_resources', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['control_monitoring_performance', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['control_enforcing_standards', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['control_adjusting_activities_on_feedback', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['coordination_harmonizing_activities', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['coordination_minimizing_conflicts', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['coordination_monitoring_disruptions', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['coordination_ensuring_consistent_comms', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['intelligence_innovation', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['intelligence_adapting_to_external_change', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['operations_execution_of_tasks', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['operations_resource_allocation', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['operations_coordination', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['operations_managing_local_issues', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['policy_setting_goals_and_priorities', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['policy_making_key_decisions', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['policy_ensuring_organizational_coherence', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['control_balancing_short_term_with_long_term', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['intelligence_long_term_planning', PALE_HIGH_TO_LOW_COLOR_MAP],
  [
    'intelligence_balancing_current_needs_with_future',
    PALE_HIGH_TO_LOW_COLOR_MAP,
  ],
  ['operations_responding_to_demands', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['policy_resolving_conflicts', PALE_HIGH_TO_LOW_COLOR_MAP],
  ['policy_maintaining_viability', PALE_HIGH_TO_LOW_COLOR_MAP],
]);

export const PALE_LIST_FIELD_DEFAULT_VALUE_TO_COLOR_MAP: Map<
  string,
  Map<string, string>
> = new Map([
  [
    'objective_priority',
    new Map([
      ['High', PALE_COLOR.green],
      ['Medium', PALE_COLOR.orange],
      ['Low', PALE_COLOR.red],
    ]),
  ],
  [
    'application_ownership_state',
    new Map([
      ['Unowned', PALE_COLOR.red],
      ['Nominated', PALE_COLOR.amber],
      ['Owned', PALE_COLOR.orange],
      ['Managed', PALE_COLOR.green],
    ]),
  ],
  [
    'confidentiality',
    new Map([
      ['Restricted', PALE_COLOR.red],
      ['Confidential', PALE_COLOR.amber],
      ['Internal', PALE_COLOR.orange],
      ['Public', PALE_COLOR.green],
    ]),
  ],
  ['ownership_accepted', PALE_YES_NO_COLOR_MAP],
  ['saa_s_alternative', PALE_YES_NO_COLOR_MAP],
  ['pass', PALE_YES_NO_COLOR_MAP],
  [
    'data_proccessing_agreement',
    new Map([
      ['Yes', PALE_COLOR.green],
      ['No', PALE_COLOR.red],
      ['Not Applicable', '#3e3e3e'],
    ]),
  ],
  [
    'ai_risk_level',
    new Map([
      ['Unacceptable Risk', PALE_COLOR.red],
      ['High Risk', PALE_COLOR.amber],
      ['Limited Risk', PALE_COLOR.orange],
      ['Minimal Risk', PALE_COLOR.green],
    ]),
  ],
  [
    'strategic_rating',
    new Map([
      ['Invest', PALE_COLOR.green],
      ['Tolerate', PALE_COLOR.orange],
      ['Migrate', PALE_COLOR.amber],
      ['Eliminate', PALE_COLOR.red],
    ]),
  ],
  ['lifecycle_phase', PALE_LIFECYCLE_PHASE_COLOR_MAP],
]);
